"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { CdmEvent } from "@/lib/sse/useSSE";
import { applyEvent } from "@/components/web/buildGraphState";
import { buildSeed } from "@/components/web/seed";
import type { GraphState } from "@/components/web/types";

/**
 * useGraphState — single source of truth for the live network state.
 *
 * Maintains the seed graph and incrementally folds new SSE events into it
 * via applyEvent. Returns the latest GraphState reference, with the
 * reference identity changing only when topology mutates (so consumers
 * can react via React's referential equality).
 *
 * Mount once at the top of the demo route and pass `graph` down to
 * components that need it (NetworkVignette in scenes, the embedded
 * InterconnectedWeb in FinalReveal). Avoids re-deriving state in each
 * consumer.
 */
export function useGraphState(events: CdmEvent[]): GraphState {
  const seedRef = useRef<GraphState | null>(null);
  if (!seedRef.current) seedRef.current = buildSeed();

  const lastAppliedRef = useRef(0);
  const graphRef = useRef<GraphState>(seedRef.current);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (events.length <= lastAppliedRef.current) return;
    let g = graphRef.current;
    for (let i = lastAppliedRef.current; i < events.length; i++) {
      g = applyEvent(g, events[i]);
    }
    lastAppliedRef.current = events.length;
    graphRef.current = g;
    setVersion((v) => v + 1);
  }, [events]);

  return useMemo(() => graphRef.current, [version]);
}
